import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { hospitalsAPI } from '../../lib/api';
import {
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  ChevronRight,
  ChevronDown,
  Lock,
  Palette,
  UserCog,
  Clock,
  CalendarDays,
  Bell,
  Stethoscope,
  ShieldCheck,
  UserRound,
  CalendarCheck,
  ListOrdered,
  Receipt,
  BedDouble,
  ClipboardList,
  IndianRupee,
  TrendingUp,
  Store,
  Wallet,
  BarChart3,
  SlidersHorizontal,
  Tag,
  Clipboard,
  Grid3X3,
  RotateCcw,
  ShieldAlert,
  Activity,
  Repeat,
  Bell as BellIcon,
  ShoppingCart,
  Wallet as WalletIcon,
  Briefcase,
  Star,
  History,
  Heart,
  Pill,
  TestTube,
  Camera,
  Droplet,
  Boxes,
  Archive,
  UtensilsCrossed,
  UserCircle,
  PartyPopper,
  ArrowRightCircle,
  MessageSquare,
  Compass,
  GitCompareArrows,
  Key,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const KLICK_LOGO = '/assets/images/klick-niramaya-logo.png';

// ============== v38.6 — Brand-color sidebar theming ==============
// Each hospital carries a `primary_color` hex (e.g. Prashanti #0e9aa7, Sushruta #1565C0).
// We derive a small palette from it so the entire left ribbon picks up the brand identity
// when that hospital is selected. When no hospital is selected (Super Admin platform view),
// we fall back to the original Klick Niramaya green.
const DEFAULT_PALETTE = {
  bg: 'linear-gradient(180deg, #061a12 0%, #091f17 40%, #071912 100%)',
  cardBg: '#0d2a1f',
  border: 'rgba(16, 185, 129, 0.25)',
  borderSoft: 'rgba(16, 185, 129, 0.15)',
  accent: '#84cc16',        // active nav highlight
  accentSoft: 'rgba(16, 185, 129, 0.15)',
  textPrimary: '#e2e8f0',
  textMuted: 'rgba(226, 232, 240, 0.65)',
  textLabel: 'rgba(226, 232, 240, 0.55)',
  badge: 'linear-gradient(90deg, rgba(16,185,129,0.6), rgba(132,204,22,0.4))',
};

const _hexToRgb = (hex) => {
  if (!hex) return null;
  const h = hex.replace('#', '').trim();
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const _shade = ({ r, g, b }, pct) => {
  // pct < 0 darkens, > 0 lightens. Returns rgb() string.
  const f = (c) => {
    if (pct < 0) return Math.round(c * (1 + pct));         // darken: c * (1-|pct|)
    return Math.round(c + (255 - c) * pct);                 // lighten
  };
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
};
const _rgba = ({ r, g, b }, a) => `rgba(${r}, ${g}, ${b}, ${a})`;

const buildPalette = (primaryHex) => {
  const rgb = _hexToRgb(primaryHex);
  if (!rgb) return DEFAULT_PALETTE;
  // We aim for the sidebar to *look* like the brand color (not a muddy near-black).
  // Top of the gradient: brand color itself. Bottom: 22% darker for subtle depth.
  const top    = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;     // brand color as-is
  const middle = _shade(rgb, -0.12);
  const bottom = _shade(rgb, -0.22);
  return {
    bg: `linear-gradient(180deg, ${top} 0%, ${middle} 55%, ${bottom} 100%)`,
    // v38.6.1 — Solid (fully opaque) darker shade for cards & dropdowns so the
    // hospital-picker dropdown does not bleed into the nav items behind it.
    cardBg: _shade(rgb, -0.25),
    border: _rgba({ r: 255, g: 255, b: 255 }, 0.22),
    borderSoft: _rgba({ r: 255, g: 255, b: 255 }, 0.12),
    accent: '#ffffff',                                      // active nav uses white for max contrast
    accentSoft: _rgba({ r: 255, g: 255, b: 255 }, 0.18),
    textPrimary: '#ffffff',
    textMuted: 'rgba(255, 255, 255, 0.75)',
    textLabel: 'rgba(255, 255, 255, 0.6)',
    badge: _rgba({ r: 255, g: 255, b: 255 }, 0.18),
  };
};

const HospitalSelector = ({ hospitals, selectedId, onChange, palette = DEFAULT_PALETTE }) => {
  const [open, setOpen] = useState(false);
  const selected = hospitals.find(h => h.id === selectedId);

  return (
    <div className="relative mt-3" data-testid="hospital-selector">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all text-left"
        style={{ background: palette.cardBg, border: `1px solid ${palette.borderSoft}` }}
      >
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: palette.textLabel }}>Manage Hospital</p>
          <p className="text-sm truncate" style={{ color: palette.textPrimary }}>{selected?.name || 'Select hospital...'}</p>
        </div>
        <ChevronDown size={14} className={cn("transition-transform flex-shrink-0 ml-2", open && "rotate-180")} style={{ color: palette.accent }} />
      </button>
      {open && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg shadow-xl max-h-48 overflow-y-auto"
          style={{ background: palette.cardBg, border: `1px solid ${palette.borderSoft}`, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}
        >
          <button
            onClick={() => { onChange(null); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-white/5"
            style={{ color: !selectedId ? palette.accent : palette.textPrimary, background: !selectedId ? palette.accentSoft : 'transparent' }}
          >
            Platform View (No Hospital)
          </button>
          {hospitals.map(h => (
            <button
              key={h.id}
              onClick={() => { onChange(h.id); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm transition-colors truncate hover:bg-white/5"
              style={{
                color: selectedId === h.id ? palette.accent : palette.textPrimary,
                background: selectedId === h.id ? palette.accentSoft : 'transparent',
              }}
              data-testid={`hospital-option-${h.id}`}
            >
              {h.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const NavGroup = ({ label, icon: Icon, children, defaultOpen = false }) => {
  const location = useLocation();
  const childPaths = children.map(c => c.path);
  const hasActive = childPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  const [open, setOpen] = useState(defaultOpen || hasActive);

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all hover:bg-white/5"
        style={{ color: hasActive ? 'var(--sidebar-accent)' : 'var(--sidebar-text-label)' }}
      >
        <Icon size={14} strokeWidth={2} />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown size={12} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <ul className="mt-0.5 space-y-0.5 ml-1">
          {children.filter(c => c.show !== false).map(item => (
            <NavItem key={item.path} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
};

const NavItem = ({ item }) => {
  const location = useLocation();
  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  return (
    <li>
      <NavLink
        to={item.path}
        className={cn(
          'flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
          isActive ? 'nav-item-active' : 'hover:bg-white/5',
        )}
        style={{
          color: isActive ? 'var(--sidebar-accent)' : 'var(--sidebar-text-muted)',
        }}
        data-testid={`nav-${item.path.replace(/\//g, '')}`}
      >
        <item.icon size={16} strokeWidth={1.5} style={isActive ? { color: 'var(--sidebar-accent)' } : undefined} />
        <span className="flex-1">{item.label}</span>
        {isActive && <ChevronRight size={12} style={{ color: 'var(--sidebar-accent)' }} />}
      </NavLink>
    </li>
  );
};

export const Sidebar = () => {
  const { user, logout, hasFeature, hospital, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState(
    () => localStorage.getItem('selectedHospitalId') || null
  );

  // v38.6 — Active hospital used to derive brand palette.
  // For super-admin: looked up from the fetched hospitals list by selectedHospitalId.
  // For regular users: comes directly from auth context (.hospital).
  const activeHospital = (() => {
    if (user?.is_super_admin) {
      return hospitals.find(h => h.id === selectedHospitalId) || null;
    }
    return hospital || null;
  })();
  const palette = buildPalette(
    activeHospital?.branding?.primary_color
      || activeHospital?.primary_color
  );

  const isSuperAdmin = user?.is_super_admin;
  const hasHospitalContext = isSuperAdmin ? true : !!user?.hospital_id;
  // Role-based access for Staff & HR module — Phase-2 RBAC consults permission codes first,
  // legacy role_code as fallback so freshly-deployed tenants without seeded perms still work.
  const userRoleCode = (user?.role_code || user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  const isHrOrAdmin = hasPermission('staff.onboard') || hasPermission('staff.manage') ||
    isSuperAdmin || ['hospital_admin', 'hr', 'it_admin', 'admin'].includes(userRoleCode);
  // Finance visibility — Phase-2 RBAC consults `finance.view`.
  const canViewFinance = hasPermission('finance.view') ||
    isSuperAdmin || ['hospital_admin', 'accountant', 'finance'].includes(userRoleCode);
  // Doctor role
  const isDoctor = userRoleCode === 'doctor';
  // Nurse role — Phase-2 RBAC: anyone with `nurse.workstation` can see the entry.
  const isNurseOrAdmin = hasPermission('nurse.workstation') ||
    isSuperAdmin || ['nurse', 'hospital_admin', 'it_admin', 'hr'].includes(userRoleCode);
  // Front Desk / Billing Staff: locked-down navigation — only Dashboard + Patient Care.
  const isFrontDeskOrBilling = ['reception', 'front_desk', 'billing_staff'].includes(userRoleCode);
  // Helper — show everything (Operations, IPD, Hospital Setup, etc.) only to roles whose scope includes them.
  const canViewBeyondPatientCare = !isFrontDeskOrBilling;
  // VIP review dashboard is for admin-tier roles only.
  const canViewVipDashboard = hasPermission('patients.toggle_vip') ||
    isSuperAdmin || ['hospital_admin', 'it_admin'].includes(userRoleCode);

  useEffect(() => {
    if (isSuperAdmin) {
      hospitalsAPI.list().then(r => setHospitals(r.data || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  const handleHospitalChange = (id) => {
    setSelectedHospitalId(id);
    if (id) {
      localStorage.setItem('selectedHospitalId', id);
    } else {
      localStorage.removeItem('selectedHospitalId');
    }
    window.location.reload();
  };

  return (
    <aside
      className="fixed left-0 top-0 z-50 h-screen w-64 flex flex-col sidebar-premium"
      data-testid="sidebar"
      data-active-hospital={activeHospital?.id || 'platform'}
      style={{
        background: palette.bg,
        color: palette.textPrimary,
        borderRight: `1px solid ${palette.borderSoft}`,
        // CSS variables consumed by nested elements + nav-item-active rule.
        '--sidebar-card-bg':    palette.cardBg,
        '--sidebar-border':     palette.border,
        '--sidebar-border-soft': palette.borderSoft,
        '--sidebar-accent':     palette.accent,
        '--sidebar-accent-soft': palette.accentSoft,
        '--sidebar-text':       palette.textPrimary,
        '--sidebar-text-muted': palette.textMuted,
        '--sidebar-text-label': palette.textLabel,
        '--sidebar-badge':      palette.badge,
      }}
    >
      {/* Branding — Logo (with embedded "Klick Niramaya HMS" wordmark). Customer-facing brand presence (never compromise on this). */}
      <div className="px-3 pt-4 pb-3 flex items-center justify-center" style={{ borderBottom: `1px solid ${palette.borderSoft}` }}>
        <button onClick={() => navigate('/dashboard')} className="cursor-pointer group" data-testid="sidebar-logo-btn" aria-label="Klick Niramaya HMS">
          <img
            src={hospital?.branding?.logo_url || hospital?.logo_url || KLICK_LOGO}
            alt={hospital?.name || 'Klick Niramaya HMS'}
            className="h-32 w-auto object-contain drop-shadow-[0_4px_20px_rgba(16,185,129,0.35)] group-hover:scale-[1.03] transition-transform duration-300"
            data-testid="sidebar-logo"
          />
        </button>
      </div>

      {/* User Info */}
      <div className="px-4 py-3" style={{ borderBottom: `1px solid ${palette.borderSoft}` }}>
        <div className="flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-xs shadow-lg"
            style={{ background: `linear-gradient(135deg, ${palette.accent}, ${palette.cardBg})` }}
          >
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: palette.textPrimary }}>{user?.first_name} {user?.last_name}</p>
            <p className="text-xs truncate" style={{ color: palette.textMuted }}>{user?.role_name || 'User'}</p>
          </div>
        </div>
        {!isSuperAdmin && user?.hospital_name && (
          <div className="mt-2 px-3 py-1.5 rounded-lg" style={{ background: palette.cardBg, border: `1px solid ${palette.borderSoft}` }}>
            <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: palette.textLabel }}>Hospital</p>
            <p className="text-xs truncate" style={{ color: palette.textPrimary }}>{user.hospital_name}</p>
          </div>
        )}
        {isSuperAdmin && (
          <>
            <div className="mt-2 px-3 py-1 rounded-lg" style={{ background: palette.badge, border: `1px solid ${palette.border}` }}>
              <p className="text-xs font-semibold tracking-wide" style={{ color: palette.textPrimary }}>Super Admin</p>
            </div>
            {/* v38.7 — When the SPA is loaded under a tenant subdomain (e.g. prashanti.klickniramaya.com)
                the hospital is locked to that tenant — hide the picker and show a static label instead. */}
            {localStorage.getItem('tenantLocked') === '1' ? (
              <div className="mt-2 px-3 py-2 rounded-lg" style={{ background: palette.cardBg, border: `1px solid ${palette.borderSoft}` }}>
                <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: palette.textLabel }}>Tenant Workspace</p>
                <p className="text-sm truncate" style={{ color: palette.textPrimary }}>{localStorage.getItem('tenantName') || activeHospital?.name || ''}</p>
              </div>
            ) : (
              <HospitalSelector hospitals={hospitals} selectedId={selectedHospitalId} onChange={handleHospitalChange} palette={palette} />
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-3 sidebar-nav-scroll">
        {/* Dashboard — per-role segregation (v34): now driven by individual
            dashboard.* permission codes that Platform Admin / Hospital Admin can
            toggle on the Roles & Permissions matrix per role per hospital. */}
        {hasHospitalContext ? (
          <NavGroup label="Dashboard" icon={LayoutDashboard} defaultOpen>
            {[
              ...(hasPermission('dashboard.view')
                ? [{ label: 'Admin Dashboard', icon: LayoutDashboard, path: '/dashboard' }]
                : []),
              ...(hasPermission('dashboard.doctor')
                ? [{ label: 'Doctor Dashboard', icon: Stethoscope, path: '/doctor-dashboard' }]
                : []),
              ...(hasPermission('dashboard.patient')
                ? [{ label: 'Patient Dashboard', icon: UserRound, path: '/patient-dashboard' }]
                : []),
              { label: 'Bed Status Dashboard', icon: BedDouble, path: '/bed-status' },
              ...(hasPermission('dashboard.nurse')
                ? [{ label: 'Nurse Workstation', icon: Heart, path: '/nurse-dashboard' }]
                : []),
              ...(hasPermission('dashboard.notice')
                ? [{ label: 'Notice Dashboard', icon: BarChart3, path: '/notice-dashboard' }]
                : []),
              ...(hasPermission('dashboard.vip')
                ? [{ label: 'VIP Patients', icon: Star, path: '/vip-patients' }]
                : []),
            ]}
          </NavGroup>
        ) : (
          <ul className="space-y-0.5 mb-2">
            <NavItem item={{ label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' }} />
          </ul>
        )}

        {/* Hospital 360° — Finance / Statistics / Staff / Department dashboards (v37b2 + v38) */}
        {hasHospitalContext && (hasPermission('dashboard.finance') || hasPermission('dashboard.statistics_360') || hasPermission('dashboard.staff_360')) && (
          <NavGroup label="Hospital 360°" icon={Compass}>
            {[
              ...(hasPermission('dashboard.finance')
                ? [{ label: 'Finance Dashboard', icon: Wallet, path: '/finance-dashboard' }] : []),
              ...(hasPermission('dashboard.statistics_360')
                ? [{ label: 'Statistics Dashboard', icon: GitCompareArrows, path: '/stats360' }] : []),
              ...(hasPermission('dashboard.staff_360')
                ? [{ label: 'Staff 360°', icon: UserRound, path: '/staff-360' }] : []),
              // v38.5 — Department Dashboard (always visible to anyone who can see this section)
              { label: 'Department Dashboard', icon: Building2, path: '/department-dashboard' },
            ]}
          </NavGroup>
        )}

        {/* My Workspace — personal tools available to ANY logged-in staff with a hospital_id. */}
        {hasHospitalContext && !user?.is_super_admin && (
          <NavGroup label="My Workspace" icon={Clock} defaultOpen>
            {[
              { label: 'My Profile', icon: UserCircle, path: '/my-profile' },
              ...(hasPermission('dashboard.my_360')
                ? [{ label: 'My 360°', icon: Compass, path: '/my-360' }] : []),
              { label: 'My Attendance', icon: Clock, path: '/my-attendance' },
              { label: 'Public Holidays', icon: CalendarDays, path: '/public-holidays' },
              ...(isDoctor ? [{ label: 'My Calendar', icon: CalendarDays, path: '/my-calendar' }] : []),
            ]}
          </NavGroup>
        )}

        {/* Patient Care — top items only (per product owner: Patients, Appointments, Payments, Vitals Monitor, Queue) */}
        {hasHospitalContext && (
          <NavGroup label="Patient Care" icon={UserRound} defaultOpen>
            {[
              { label: 'Patients', icon: UserRound, path: '/patients' },
              { label: 'Appointments', icon: CalendarCheck, path: '/appointments' },
              { label: 'Payments', icon: CreditCard, path: '/payments' },
              { label: 'Invoice History', icon: History, path: '/invoice-history' },
              { label: 'Refunds', icon: RotateCcw, path: '/refunds' },
              { label: 'Vitals Monitor', icon: Activity, path: '/vitals' },
              { label: 'Queue', icon: ListOrdered, path: '/queue' },
              ...(hasPermission('prescriptions.view') ? [{ label: 'Prescriptions', icon: Pill, path: '/prescriptions' }] : []),
            ]}
          </NavGroup>
        )}

        {/* Patient Care Advanced — rest of the patient-care surface; hidden from Front Desk / Billing */}
        {hasHospitalContext && canViewBeyondPatientCare && (
          <NavGroup label="Patient Care Advanced" icon={Stethoscope}>
            {[
              ...(isDoctor ? [{ label: 'My Calendar', icon: CalendarDays, path: '/my-calendar' }] : []),
              { label: 'Recurring Series', icon: Repeat, path: '/recurring-appointments' },
              { label: 'Reminders', icon: BellIcon, path: '/reminders' },
              { label: 'Service Catalog', icon: Tag, path: '/service-catalog' },
              { label: 'Consultation Types', icon: Clipboard, path: '/consultation-types' },
              { label: 'Allergies', icon: ShieldAlert, path: '/allergies' },
            ]}
          </NavGroup>
        )}

        {/* Clinical Modules (C1-C9) — Pharmacy, Lab, Imaging, Insurance, Blood Bank, Store, Mortuary, Canteen.
            Each child has its own permission gate so roles see only the entries they can access. */}
        {hasHospitalContext && (() => {
          const clinicalChildren = [
            ...(hasPermission('pharmacy.view') ? [{ label: 'Pharmacy', icon: Pill, path: '/pharmacy' }] : []),
            ...(hasPermission('lab.view') ? [{ label: 'Lab', icon: TestTube, path: '/lab' }] : []),
            ...(hasPermission('imaging.view') ? [{ label: 'Imaging / Radiology', icon: Camera, path: '/imaging' }] : []),
            ...(hasPermission('insurance.view') ? [{ label: 'Insurance / TPA', icon: ShieldCheck, path: '/insurance' }] : []),
            ...(hasPermission('bloodbank.view') ? [{ label: 'Blood Bank', icon: Droplet, path: '/bloodbank' }] : []),
            ...(hasPermission('store.view') ? [{ label: 'Central Store', icon: Boxes, path: '/store' }] : []),
            ...(hasPermission('mortuary.view') ? [{ label: 'Mortuary', icon: Archive, path: '/mortuary' }] : []),
            ...(hasPermission('canteen.view') ? [{ label: 'Canteen', icon: UtensilsCrossed, path: '/canteen' }] : []),
          ];
          return clinicalChildren.length > 0 ? (
            <NavGroup label="Clinical Modules" icon={Pill}>{clinicalChildren}</NavGroup>
          ) : null;
        })()}

        {hasHospitalContext && (
          <NavGroup label="IPD" icon={BedDouble}>
            {[
              { label: 'IPD Referrals', icon: ArrowRightCircle, path: '/ipd-referrals' },
              { label: 'Wards & Beds', icon: BedDouble, path: '/wards' },
              { label: 'Admissions', icon: ClipboardList, path: '/admissions' },
              { label: 'Discharge Summary', icon: Clipboard, path: '/discharge-summaries' },
            ]}
          </NavGroup>
        )}

        {/* Staff & HR */}
        {hasHospitalContext && isHrOrAdmin && (
          <NavGroup label="Staff & HR" icon={UserCog}>
            {[
              { label: 'Staff Management', icon: UserCog, path: '/staff' },
              { label: 'Bulk Staff Import', icon: UploadCloud, path: '/bulk-staff-import' },
              { label: 'Attendance', icon: Clock, path: '/attendance' },
              { label: 'Leave', icon: CalendarDays, path: '/leave' },
              { label: 'Shifts & Roster', icon: CalendarDays, path: '/shifts' },
              { label: 'Payroll', icon: WalletIcon, path: '/payroll' },
              { label: 'Doctor Config', icon: Stethoscope, path: '/doctor-config' },
              { label: 'Doctor Availability', icon: CalendarCheck, path: '/doctor-availability' },
              { label: 'KYC Compliance', icon: ShieldCheck, path: '/kyc-compliance' },
              { label: 'Celebrations', icon: PartyPopper, path: '/celebrations-settings' },
            ]}
          </NavGroup>
        )}

        {/* Finance */}
        {hasHospitalContext && canViewFinance && (
          <NavGroup label="Finance" icon={IndianRupee}>
            {[
              { label: 'Profit & Loss', icon: BarChart3, path: '/profit-loss' },
              { label: 'Invoices', icon: Receipt, path: '/invoices' },
              { label: 'Revenue & Payouts', icon: TrendingUp, path: '/revenue' },
              { label: 'Fee Category (Service Catalog)', icon: Tag, path: '/service-catalog' },
              { label: 'Finance Config', icon: SlidersHorizontal, path: '/finance-config' },
              { label: 'Billing Config', icon: IndianRupee, path: '/billing-config' },
            ]}
          </NavGroup>
        )}

        {/* Operations — not in Front Desk / Billing scope */}
        {hasHospitalContext && canViewBeyondPatientCare && (
          <NavGroup label="Operations" icon={Store}>
            {[
              { label: 'Departments', icon: Building2, path: '/departments' },
              { label: 'Vendors', icon: Store, path: '/vendors' },
              { label: 'Purchase Orders', icon: ShoppingCart, path: '/purchase-orders' },
              { label: 'Expenses', icon: Wallet, path: '/expenses' },
            ]}
          </NavGroup>
        )}

        {/* Integrations — partner API keys etc. Hospital Admin / IT Admin only. */}
        {hasHospitalContext && isHrOrAdmin && (
          <NavGroup label="Integrations" icon={Key}>
            {[
              { label: 'Partner API Keys', icon: Key, path: '/integrations/partner-keys' },
            ]}
          </NavGroup>
        )}

        {/* Hospital Setup — not in Front Desk / Billing scope */}
        {hasHospitalContext && canViewBeyondPatientCare && (
          <NavGroup label="Hospital Setup" icon={Settings}>
            {[
              { label: 'Branding', icon: Palette, path: '/branding' },
              { label: 'Notices', icon: Bell, path: '/notices' },
              ...(isHrOrAdmin ? [{ label: 'Access Control', icon: UserCog, path: '/access-control' }] : []),
              { label: 'Security', icon: ShieldCheck, path: '/security' },
              { label: 'Audit Logs', icon: Shield, path: '/audit-logs' },
            ]}
          </NavGroup>
        )}

        {/* Platform (Super Admin) */}
        {isSuperAdmin && (
          <NavGroup label="Platform" icon={Shield}>
            {[
              { label: 'Platform Health', icon: Activity, path: '/platform-health' },
              { label: 'Hospitals', icon: Building2, path: '/hospitals' },
              { label: 'Users', icon: Users, path: '/users' },
              { label: 'Roles', icon: Shield, path: '/roles' },
              { label: 'Subscriptions', icon: CreditCard, path: '/subscriptions' },
              { label: 'Audit Logs', icon: FileText, path: '/audit-logs' },
              { label: 'Feedback Dashboard', icon: MessageSquare, path: '/feedback-dashboard' },
              { label: 'Bulk Staff Import', icon: UploadCloud, path: '/bulk-staff-import' },
              { label: 'Production Cleanup', icon: Trash2, path: '/production-cleanup' },
            ]}
          </NavGroup>
        )}

        {/* Hospital Admin / HR also gets Feedback (own hospital) */}
        {!isSuperAdmin && hasHospitalContext && isHrOrAdmin && (
          <NavGroup label="Insights" icon={MessageSquare}>
            {[
              { label: 'Feedback Dashboard', icon: MessageSquare, path: '/feedback-dashboard' },
            ]}
          </NavGroup>
        )}
      </nav>

      {/* Logout */}
      <div className="p-3" style={{ borderTop: `1px solid ${palette.borderSoft}` }}>
        <button
          onClick={logout}
          className="flex items-center gap-3 w-full px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-500/15 hover:text-red-200 transition-all duration-200"
          style={{ color: palette.textMuted }}
          data-testid="logout-btn"
        >
          <LogOut size={18} strokeWidth={1.5} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
